import React from "react";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { getOrderProfit } from "../../ordersSlice";
import { ProfitStatus } from "../../../../models";

import "./OrderProfitCard.scss";

const money = (n: number) =>
  (n < 0 ? "-" : "") +
  "$" +
  Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Human-friendly leg duration: 75 min -> "1hr 15min", 60 -> "1hr", 42 -> "42min".
const fmtDuration = (seconds: number) => {
  const mins = Math.round(seconds / 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}hr ${m}min`;
  if (h) return `${h}hr`;
  return `${m}min`;
};

const STATUS_LABEL: Record<ProfitStatus, string> = {
  final: "Final",
  preliminary: "Preliminary",
  incomplete: "Incomplete",
  internal: "Internal",
};

// Human-friendly labels for invoice types (svc-048 revenue breakdown). Unknown
// types fall back to a title-cased version of the raw value.
const INVOICE_TYPE_LABEL: Record<string, string> = {
  dumpster_rental: "Dumpster Rental",
  weight_overage: "Weight Overage",
  custom: "Custom",
};
const invoiceTypeLabel = (type: string) =>
  INVOICE_TYPE_LABEL[type] ??
  type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

interface Props {
  orderId: string;
  // "default" = light card (Profit tab); "dark" = the dark billing-node panel
  // used in the timeline layout. Styling only — same data/logic either way.
  variant?: "default" | "dark";
}

// Profit tab content. Lazy-loads svc-037 GET /orders/{id}/profit (which may hit the
// maps provider on a cache miss) only when this card mounts — i.e. when the Profit
// tab is first opened — so it never slows the main order-details load.
export function OrderProfitCard({ orderId, variant = "default" }: Props) {
  const dispatch = useAppDispatch();
  const profit = useAppSelector((s) => s.orders.orderProfit);
  const api = useAppSelector((s) => s.orders.getOrderProfitAPI);
  const [showLegs, setShowLegs] = React.useState(false);

  React.useEffect(() => {
    dispatch(getOrderProfit(orderId));
  }, [orderId, dispatch]);

  if (api.loading && !profit) {
    return (
      <div className={`order-profit-card ${variant} loading`}>Loading profit…</div>
    );
  }
  if (api.error && !profit) {
    return (
      <div className={`order-profit-card ${variant} error`}>
        Couldn’t load profit. Please try again.
      </div>
    );
  }
  if (!profit) return null;

  const labor = profit.legs.reduce((s, l) => s + l.labor, 0);
  const fuel = profit.legs.reduce((s, l) => s + l.fuel, 0);
  const internal = profit.status === "internal";
  const receipts = profit.dumpEvents.length;

  return (
    <div className={`order-profit-card ${variant}`}>
      <div className={`profit-status status-${profit.status}`}>
        {STATUS_LABEL[profit.status] ?? profit.status}
      </div>

      <div className="profit-headline">
        <span className="profit-amount">{money(profit.profit)}</span>
        <span className="profit-sub">profit after dump cost</span>
        {!internal && (
          <span className="profit-margin">{Math.round(profit.marginPct)}% margin</span>
        )}
      </div>

      <div className="profit-lines">
        <div className="profit-line">
          <span className="pl-label">Revenue</span>
          <span className="pl-value">{money(profit.revenue)}</span>
        </div>
        {profit.revenueBreakdown.invoices.map((inv, i) => (
          <div className="profit-line profit-subline" key={i}>
            <span className="pl-label">{invoiceTypeLabel(inv.type)}</span>
            <span className="pl-value">{money(inv.amount)}</span>
          </div>
        ))}
        <div className="profit-line">
          <span className="pl-label">
            Dump cost{receipts ? ` (${receipts} receipt${receipts > 1 ? "s" : ""})` : ""}
          </span>
          <span className="pl-value neg">−{money(profit.dumpCost)}</span>
        </div>
        <div className="profit-line">
          <span className="pl-label">Labor</span>
          <span className="pl-value neg">−{money(labor)}</span>
        </div>
        <div className="profit-line">
          <span className="pl-label">Fuel</span>
          <span className="pl-value neg">−{money(fuel)}</span>
        </div>
      </div>

      {profit.annotations.length > 0 && (
        <ul className="profit-annotations">
          {profit.annotations.map((a, i) => (
            <li key={i}>{a}</li>
          ))}
        </ul>
      )}

      <button
        className="profit-legs-toggle"
        onClick={() => setShowLegs((v) => !v)}
      >
        {showLegs ? "▾" : "▸"} Legs ({profit.legs.length})
      </button>

      {showLegs && (
        <table className="profit-legs">
          <thead>
            <tr>
              <th>Leg</th>
              <th>From → To</th>
              <th>Time</th>
              <th>Miles</th>
              <th>Labor</th>
              <th>Fuel</th>
            </tr>
          </thead>
          <tbody>
            {profit.legs.map((l, i) => (
              <tr key={i}>
                <td>
                  {l.kind}
                  {l.annotations.length > 0 && (
                    <span className="leg-note"> · {l.annotations.join("; ")}</span>
                  )}
                </td>
                <td>
                  {l.from} → {l.to}
                </td>
                <td>{fmtDuration(l.seconds)}</td>
                <td>{l.miles.toFixed(1)}</td>
                <td>{money(l.labor)}</td>
                <td>{money(l.fuel)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
